import type { ReactNode } from "react";
import type { ReAnalysisBody } from "./re-api.js";

/**
 * FIX-011 / RE Stage 4 - the FULL Grok analysis, from the data Emma OS already stored (nothing is analysed again to display it). Every key of Part A and Part B is rendered
 * - arrays of records as tables, `{ value, confidence, source, unresolved }` facts as a value with its confidence / "unresolved" tag - and the complete JSON is also
 * available verbatim, so nothing the analysis contains can be omitted by the screen. An "Overview" and the main character are lifted to the top for reading.
 */
const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);

/** `{ value, confidence?, source?, unresolved?, reason? }` - how Grok marks a single observation. */
function isFact(v: unknown): v is Record<string, unknown> & { value: unknown } {
  return isRecord(v) && "value" in v && Object.keys(v).every((k) => ["value", "confidence", "source", "unresolved", "reason"].includes(k));
}

const LABELS: Record<string, string> = {
  source_metadata: "Source metadata", people: "People", environment: "Environment", lighting: "Lighting", color_characteristics: "Color", atmosphere: "Atmosphere", pacing: "Pacing", audio: "Audio",
  timeline: "Timeline (events, speech, expressions, gaze, pose, camera)", shots: "Shots & camera framing", camera_characteristics: "Camera characteristics", imperfections: "Imperfections", background_activity: "Background activity",
  end_behavior: "How it ends", reconstruction_constraints: "Reconstruction constraints", word_timings: "Words & timing", on_screen_text: "Text on screen", accessories_positions: "Accessories & their positions",
  body_and_head_movements: "Body & head movements", facial_expression_changes: "Facial expressions", gaze_changes: "Gaze", camera_motion_observed: "Camera motion observed", non_speech_sounds: "Non-speech sounds",
  characters_and_positions: "Characters & positions",
};
const label = (key: string): string => LABELS[key] ?? key.replace(/_/g, " ");

function Scalar({ value }: { readonly value: unknown }): ReactNode {
  if (value === null || value === undefined) return <span className="muted">—</span>;
  if (typeof value === "boolean") return <span>{value ? "yes" : "no"}</span>;
  return <span>{String(value)}</span>;
}

function FactView({ fact }: { readonly fact: Record<string, unknown> & { value: unknown } }): ReactNode {
  const unresolved = fact["unresolved"] === true;
  return (
    <span data-testid="re-fact">
      {fact.value === null ? <span className="muted">not determined{typeof fact["reason"] === "string" ? ` (${fact["reason"]})` : ""}</span> : <Node value={fact.value} />}
      {typeof fact["confidence"] === "number" ? <small className="muted"> · {Math.round((fact["confidence"] as number) * 100)}%</small> : null}
      {typeof fact["source"] === "string" ? <small className="muted"> · {String(fact["source"])}</small> : null}
      {unresolved ? <small style={{ marginLeft: 6 }} className="muted">[unresolved]</small> : null}
    </span>
  );
}

function Node({ value }: { readonly value: unknown }): ReactNode {
  if (isFact(value)) return <FactView fact={value} />;
  if (Array.isArray(value)) return <ArrayView items={value} />;
  if (isRecord(value)) return <RecordView record={value} />;
  return <Scalar value={value} />;
}

function RecordView({ record }: { readonly record: Record<string, unknown> }): ReactNode {
  const keys = Object.keys(record);
  if (keys.length === 0) return <span className="muted">—</span>;
  return (
    <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "minmax(120px, max-content) 1fr", columnGap: 12, rowGap: 4 }}>
      {keys.map((k) => (
        <div key={k} style={{ display: "contents" }}>
          <dt className="muted">{label(k)}</dt>
          <dd style={{ margin: 0 }}><Node value={record[k]} /></dd>
        </div>
      ))}
    </dl>
  );
}

function ArrayView({ items }: { readonly items: readonly unknown[] }): ReactNode {
  if (items.length === 0) return <span className="muted">none</span>;
  if (items.every((i) => !isRecord(i) && !Array.isArray(i))) return <span>{items.map((i, n) => (<span key={n}>{n > 0 ? "; " : ""}<Scalar value={i} /></span>))}</span>;
  const rows = items.filter(isRecord);
  if (rows.length === items.length) {
    const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))];
    return (
      <div style={{ overflowX: "auto" }}>
        <table data-testid="re-table">
          <thead><tr>{columns.map((c) => <th key={c}>{label(c)}</th>)}</tr></thead>
          <tbody>{rows.map((r, n) => (<tr key={n}>{columns.map((c) => <td key={c}>{c in r ? <Node value={r[c]} /> : <span className="muted">—</span>}</td>)}</tr>))}</tbody>
        </table>
      </div>
    );
  }
  return <ul style={{ margin: 0, paddingLeft: 18 }}>{items.map((i, n) => <li key={n}><Node value={i} /></li>)}</ul>;
}

function Section({ title, testId, children }: { readonly title: string; readonly testId: string; readonly children: ReactNode }): ReactNode {
  return (
    <section className="card" data-testid={testId} style={{ marginBottom: 12 }}>
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      {children}
    </section>
  );
}

const factValue = (v: unknown): unknown => (isFact(v) ? v.value : v);

export function ReAnalysisView({ analysis, personaName }: { readonly analysis: ReAnalysisBody; readonly personaName: string }): ReactNode {
  const a = analysis.part_a;
  const b = analysis.part_b;
  const meta = isRecord(a["source_metadata"]) ? a["source_metadata"] : {};
  const people = Array.isArray(a["people"]) ? a["people"].filter(isRecord) : [];
  const mainIndex = Math.max(0, people.findIndex((p) => /main/i.test(String(factValue(p["role"]) ?? ""))));
  const timeline = Array.isArray(a["timeline"]) ? a["timeline"].filter(isRecord) : [];
  const dialogue = timeline.filter((t) => typeof t["line"] === "string" && t["line"] !== "");
  const constraints = isRecord(a["reconstruction_constraints"]) ? a["reconstruction_constraints"] : {};
  // FIX-072: `must_preserve` (older analyses), `must_preserve_action` (always kept) and `must_preserve_look` (source look, a proposal may replace it). Facts are shown by their value.
  const listOf = (key: string): string[] => (Array.isArray(constraints[key]) ? (constraints[key] as unknown[]).map((x) => factValue(x)).filter((x) => x !== null && x !== undefined && typeof x !== "object").map(String) : []);
  const mustPreserve = listOf("must_preserve");
  const mustPreserveAction = listOf("must_preserve_action");
  const mustPreserveLook = listOf("must_preserve_look");

  return (
    <div data-testid="re-analysis">
      <Section title="Overview" testId="re-overview">
        <RecordView record={{ duration_seconds: meta["duration_seconds"], aspect_ratio: meta["aspect_ratio"], overall_clip_style: meta["overall_clip_style"], visual_format_notes: meta["visual_format_notes"], atmosphere: a["atmosphere"], pacing: a["pacing"], color_characteristics: a["color_characteristics"], end_behavior: a["end_behavior"] }} />
        {mustPreserve.length > 0 ? <p style={{ marginBottom: 0 }}><strong>Must be preserved:</strong> {mustPreserve.join("; ")}</p> : null}
        {mustPreserveAction.length > 0 ? <p style={{ marginBottom: 0 }} data-testid="re-must-preserve-action"><strong>Must be preserved (action, always kept):</strong> {mustPreserveAction.join("; ")}</p> : null}
        {mustPreserveLook.length > 0 ? <p style={{ marginBottom: 0 }} data-testid="re-must-preserve-look"><strong>Source look (a proposal may replace it):</strong> {mustPreserveLook.join("; ")}</p> : null}
      </Section>

      <Section title="Characters" testId="re-characters">
        {people.length === 0 ? <p className="muted">No people were identified.</p> : null}
        {people.map((p, i) => (
          <div key={i} style={{ marginBottom: 8 }} data-testid={i === mainIndex ? "re-main-character" : "re-character"}>
            {i === mainIndex ? <p style={{ margin: "0 0 4px" }}><strong>Main character</strong> — <em>will be replaced by {personaName}</em></p> : <p style={{ margin: "0 0 4px" }}><strong>Character {i + 1}</strong></p>}
            <RecordView record={p} />
          </div>
        ))}
      </Section>

      <Section title={`Dialogue (${dialogue.length} line${dialogue.length === 1 ? "" : "s"})`} testId="re-dialogue">
        {dialogue.length === 0 ? <p className="muted">No spoken dialogue.</p> : (
          <table><thead><tr><th>From</th><th>To</th><th>Speaker</th><th>Line</th><th>Delivery / tone</th></tr></thead>
            <tbody>{dialogue.map((t, i) => (<tr key={i}><td>{String(t["start_time"] ?? "")}</td><td>{String(t["end_time"] ?? "")}</td><td>{String(t["speaker"] ?? "—")}</td><td>{String(t["line"])}</td><td>{String(t["delivery"] ?? "—")}</td></tr>))}</tbody></table>
        )}
      </Section>

      <h4>Part A — canonical analysis</h4>
      {Object.keys(a).map((key) => <Section key={key} title={label(key)} testId={`re-a-${key}`}><Node value={a[key]} /></Section>)}
      <h4>Part B — detailed observations</h4>
      {Object.keys(b).map((key) => <Section key={key} title={label(key)} testId={`re-b-${key}`}><Node value={b[key]} /></Section>)}

      <details data-testid="re-raw-json" style={{ marginTop: 8 }}>
        <summary>Complete analysis as stored (JSON)</summary>
        <pre style={{ overflowX: "auto", maxHeight: 480 }}>{JSON.stringify({ part_a: a, part_b: b }, null, 2)}</pre>
      </details>
    </div>
  );
}
